import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Users, Target, Shield, Cog } from 'lucide-react';
import { Button } from '../components/ui/button';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

// Hero Section
const AboutHero = () => {
  return (
    <section className="pt-[140px] pb-[120px] md:pt-[180px] bg-background" data-testid="about-hero">
      <div className="arch-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm uppercase tracking-widest text-gold mb-4">About Agastya Ventures</p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-white mb-6 leading-tight">
              Building Trust,<br />
              <span className="text-gold">Delivering Excellence</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              Agastya Ventures is your trusted, start-to-end real estate partner, delivering 
              comprehensive solutions across India's residential and commercial markets. We manage 
              the entire property transaction lifecycle under one roof—from project curation, due 
              diligence, and financing support to documentation, registration, and post-sale assistance.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              With deep local market expertise and a relationship-driven approach, we focus on 
              long-term value creation. Our commitment ensures every client enjoys a smooth, 
              transparent, and hassle-free real estate journey.
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="aspect-square relative">
              <img
                src="/mumbai-skyline.jpg"
                alt="Mumbai Skyline"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Team Section
const TeamSection = () => {
  const team = [
    {
      name: 'Chinmay Rane',
      role: 'Director',
      department: 'PMC & Construction',
      image: '/team/chinmay-rane.png',
      description: 'Leading project management and construction operations with expertise in delivering complex real estate projects.'
    },
    {
      name: 'Sahil Shrotri',
      role: 'Director',
      department: 'Architecture & Design',
      image: '/team/sahil-shrotri.png',
      description: 'Driving architectural excellence and innovative design solutions for residential and commercial developments.'
    }
  ];

  return (
    <section id="team" className="arch-section bg-card" data-testid="team-section">
      <div className="arch-container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-sm uppercase tracking-widest text-gold mb-4">Leadership</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-white">Our Team</h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {team.map((member, index) => (
            <motion.div
              key={member.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group"
              data-testid={`team-member-${index}`}
            >
              <div className="relative aspect-[3/4] overflow-hidden mb-6">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-full object-cover transition-transform duration-[800ms] ease-out group-hover:scale-[1.02]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              </div>
              <p className="text-xs uppercase tracking-widest text-gold mb-2">{member.department}</p>
              <h3 className="text-2xl font-serif text-white mb-1">{member.name}</h3>
              <p className="text-muted-foreground text-sm mb-4">{member.role}</p>
              <p className="text-muted-foreground leading-relaxed">{member.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Philosophy Section
const PhilosophySection = () => {
  const philosophies = [
    {
      icon: Users,
      title: 'Client-Centered Design',
      description: 'We build with people, not just for projects. Every decision is made with our clients\' best interests at heart.'
    },
    {
      icon: Target,
      title: 'Design with Purpose & Practicality',
      description: 'Good design must look beautiful, function efficiently, and age gracefully. We balance aesthetics with functionality.'
    },
    {
      icon: Shield,
      title: 'Transparency, Ethics & Compliance',
      description: 'Trust is built through honesty, documentation, and lawful processes. We maintain complete transparency in all dealings.'
    },
    {
      icon: Cog,
      title: 'Technical Excellence & Process-Driven Execution',
      description: 'Strong processes deliver predictable outcomes. Our systematic approach ensures quality and timely delivery.'
    }
  ];

  return (
    <section id="philosophy" className="arch-section bg-background" data-testid="philosophy-section">
      <div className="arch-container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-sm uppercase tracking-widest text-gold mb-4">Our Values</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-white">Our Philosophy</h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {philosophies.map((item, index) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="p-8 md:p-12 border border-white/10 hover:border-gold/30 transition-colors group"
              data-testid={`philosophy-card-${index}`}
            >
              <item.icon className="w-10 h-10 text-gold mb-6" strokeWidth={1.5} />
              <h3 className="text-xl font-serif text-white mb-4 group-hover:text-gold transition-colors">
                {item.title}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {item.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const AboutCTA = () => {
  return (
    <section className="arch-section bg-card" data-testid="about-cta">
      <div className="arch-container text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-white mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Let's discuss how we can bring your real estate vision to life with our expertise and dedication.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact">
              <Button
                className="bg-gold text-black hover:bg-gold-light rounded-none px-8 py-6 text-sm uppercase tracking-widest font-bold"
                data-testid="about-contact-btn"
              >
                Get in Touch
                <ArrowUpRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/portfolio">
              <Button
                variant="outline"
                className="border-white/20 text-white hover:bg-white hover:text-black rounded-none px-8 py-6 text-sm uppercase tracking-widest"
                data-testid="about-portfolio-btn"
              >
                View Our Work
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Main About Page
const AboutPage = () => {
  return (
    <main data-testid="about-page">
      <AboutHero />
      <TeamSection />
      <PhilosophySection />
      <AboutCTA />
    </main>
  );
};

export default AboutPage;
